import React, { useState, useEffect, useCallback } from 'react';
import { authFetch, API } from '../../api';
import { useAuth } from '../../AuthContext';
import { Ruler, Plus, Trash2, Clock, TrendingUp } from 'lucide-react';

export default function CustomMeasurables({ playerId, playerPosition }) {
  const { user } = useAuth();
  const [measurables, setMeasurables] = useState([]);
  const [showAddEntry, setShowAddEntry] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [entryForm, setEntryForm] = useState({ value: '', recorded_date: new Date().toISOString().split('T')[0], notes: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  const canEnter = user?.role !== 'parent';

  const loadData = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/measurables/entries/${playerId}`);
      if (res.ok) setMeasurables(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [playerId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddEntry = async (measId) => {
    setError(''); setSuccess('');
    if (!entryForm.value) { setError('Value is required'); return; }
    try {
      const res = await authFetch(`${API}/api/measurables/entries/${playerId}`, {
        method: 'POST',
        body: JSON.stringify({
          measurable_id: measId,
          value: parseFloat(entryForm.value),
          recorded_date: entryForm.recorded_date,
          notes: entryForm.notes
        })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      setSuccess('Recorded');
      setEntryForm({ value: '', recorded_date: new Date().toISOString().split('T')[0], notes: '' });
      setShowAddEntry(null);
      loadData();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) { setError(err.message); }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await authFetch(`${API}/api/measurables/entries/${entryId}`, { method: 'DELETE' });
      loadData();
    } catch (err) { console.error(err); }
  };

  const toggleHistory = (id) => {
    setExpandedHistory(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getTrend = (entries) => {
    if (entries.length < 2) return null;
    const latest = entries[0].value;
    const previous = entries[1].value;
    const diff = latest - previous;
    if (diff > 0) return { color: '#00E676', symbol: '▲' };
    if (diff < 0) return { color: '#EF5350', symbol: '▼' };
    return { color: '#FFD600', symbol: '—' };
  };

  if (loading) return <div className="text-gray-400 text-sm py-4">Loading measurables...</div>;
  if (measurables.length === 0) return null; // Don't show section if no measurables defined for this school

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white font-semibold text-base flex items-center gap-2">
          <Ruler className="w-4 h-4 text-[#00D4FF]" />
          Custom Measurables
        </h3>
      </div>

      {success && <div className="bg-green-900/30 border border-green-700/50 text-green-300 px-3 py-2 rounded-lg mb-3 text-xs">{success}</div>}
      {error && !showAddEntry && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-3 py-2 rounded-lg mb-3 text-xs">{error}</div>}

      <div className="space-y-3">
        {measurables.map(m => {
          const trend = getTrend(m.entries);
          const isExpanded = expandedHistory[m.id];
          const isRecording = showAddEntry === m.id;

          return (
            <div key={m.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
              {/* Measurable Header */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="text-white font-semibold text-sm">{m.name}</div>
                  {m.unit && <span className="text-gray-500 text-xs">({m.unit})</span>}
                  {m.position !== 'ALL' && (
                    <span className="px-1.5 py-0.5 rounded bg-[#243B55] text-[#00D4FF] text-xs">{m.position}</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {m.latest ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[#00D4FF] font-bold text-lg">
                        {m.latest.value === Math.floor(m.latest.value) ? m.latest.value : m.latest.value.toFixed(2)}
                      </span>
                      {m.unit && <span className="text-gray-400 text-xs">{m.unit}</span>}
                      {trend && <span className="text-xs" style={{ color: trend.color }}>{trend.symbol}</span>}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-xs">No data</span>
                  )}

                  <div className="flex items-center gap-1 ml-2">
                    {m.entries.length > 0 && (
                      <button onClick={() => toggleHistory(m.id)} title="View history"
                        className="p-1.5 text-gray-400 hover:text-[#00D4FF] hover:bg-[#243B55] rounded transition-colors flex items-center">
                        <Clock className="w-3.5 h-3.5" />
                        <span className="text-xs ml-0.5">{m.entries.length}</span>
                      </button>
                    )}
                    {canEnter && (
                      <button onClick={() => { setShowAddEntry(isRecording ? null : m.id); setEntryForm({ value: '', recorded_date: new Date().toISOString().split('T')[0], notes: '' }); setError(''); }}
                        title="Record value"
                        className="p-1.5 text-gray-400 hover:text-[#00E676] hover:bg-[#243B55] rounded transition-colors">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Record Entry Form */}
              {isRecording && (
                <div className="px-4 py-3 border-t border-[#546E7A]/20 bg-[#0D1229]">
                  {error && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-3 py-1.5 rounded mb-2 text-xs">{error}</div>}
                  <div className="flex items-end gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Value{m.unit ? ` (${m.unit})` : ''}</label>
                      <input type="number" step="any" value={entryForm.value}
                        onChange={(e) => setEntryForm({...entryForm, value: e.target.value})}
                        className="w-28 px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                        placeholder="0" autoFocus />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Date</label>
                      <input type="date" value={entryForm.recorded_date}
                        onChange={(e) => setEntryForm({...entryForm, recorded_date: e.target.value})}
                        className="px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
                      <input type="text" value={entryForm.notes}
                        onChange={(e) => setEntryForm({...entryForm, notes: e.target.value})}
                        className="w-full px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                        placeholder="Camp, practice, combine..." />
                    </div>
                    <button onClick={() => handleAddEntry(m.id)}
                      className="px-4 py-1.5 bg-[#00E676] text-[#0A0E27] rounded-lg font-semibold text-xs hover:bg-[#00E676]/90">Record</button>
                    <button onClick={() => setShowAddEntry(null)}
                      className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-xs">Cancel</button>
                  </div>
                </div>
              )}

              {/* History */}
              {isExpanded && m.entries.length > 0 && (
                <div className="px-4 py-2 border-t border-[#546E7A]/20 bg-[#0D1229]">
                  <div className="text-xs text-gray-400 font-semibold mb-2 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> History ({m.entries.length} recording{m.entries.length !== 1 ? 's' : ''})
                  </div>
                  <div className="space-y-1">
                    {m.entries.map((entry, idx) => (
                      <div key={entry.id} className="flex items-center justify-between text-xs py-1">
                        <div className="flex items-center gap-3">
                          <span className="text-gray-500 w-20">{entry.recorded_date || '—'}</span>
                          <span className="text-white font-bold">
                            {entry.value === Math.floor(entry.value) ? entry.value : entry.value.toFixed(2)}
                            {m.unit ? ` ${m.unit}` : ''}
                          </span>
                          {idx > 0 && (
                            <span className="text-xs" style={{ color: entry.value > m.entries[idx-1].value ? '#00E676' : entry.value < m.entries[idx-1].value ? '#EF5350' : '#FFD600' }}>
                              {entry.value > m.entries[idx-1].value ? '▲' : entry.value < m.entries[idx-1].value ? '▼' : '—'}
                            </span>
                          )}
                          {entry.notes && <span className="text-gray-500 italic">— {entry.notes}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {entry.recorded_by && <span className="text-gray-500">{entry.recorded_by}</span>}
                          {canEnter && (
                            <button onClick={() => handleDeleteEntry(entry.id)}
                              className="p-1 text-gray-500 hover:text-red-400 rounded"><Trash2 className="w-3 h-3" /></button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
